/**
 * Google Analytics 4 (gtag) identity helpers.
 *
 * Promise wrappers around `gtag('get', measurementId, key, cb)` so the parent
 * page can read a user's GA4 `client_id` and `session_id` and forward them
 * across boundaries the GA4 linker can't auto-decorate (e.g. iframe `src`
 * URLs set by JS — see `docs/specs/tixologi-widget.md`).
 *
 * Retry + Sentry-on-timeout pattern mirrors `./google-ads.ts`.
 */

import { SentryFeatureTags, logException } from "@punchup/sentry";

type GtagGetCallback = (value: string | undefined) => void;
type WindowWithGtag = Window &
  typeof globalThis & {
    gtag?: (
      command: "get",
      targetId: string,
      fieldName: string,
      callback: GtagGetCallback
    ) => void;
  };

type GtagGetKey = "client_id" | "session_id";

const MAX_GTAG_RETRIES = 3;
const GTAG_RETRY_DELAY_MS = 500;

const gtagGet = (
  measurementId: string,
  key: GtagGetKey
): Promise<string | null> =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }

    let retries = 0;
    const attempt = () => {
      const gtag = (window as WindowWithGtag).gtag;
      if (typeof gtag === "function") {
        gtag("get", measurementId, key, (value) => {
          resolve(value ?? null);
        });
        return;
      }

      if (retries >= MAX_GTAG_RETRIES) {
        logException(
          new Error(`gtag('get') unavailable after retries: ${key}`),
          {
            tags: { feature: SentryFeatureTags.GoogleAnalytics },
            extra: { measurementId, key, retries },
          }
        );
        resolve(null);
        return;
      }

      retries += 1;
      setTimeout(attempt, GTAG_RETRY_DELAY_MS);
    };

    attempt();
  });

export const getGa4ClientId = (measurementId: string): Promise<string | null> =>
  gtagGet(measurementId, "client_id");

export const getGa4SessionId = (
  measurementId: string
): Promise<string | null> => gtagGet(measurementId, "session_id");
